import React, { useEffect, useId, useRef, useState } from 'react';
import { toast } from 'react-toastify';

import { Container, ContainerSugestoes, Input, Sugestao } from './styles';

import api from '../../services/api';
import { Path, UseFormRegister, UseFormSetValue } from 'react-hook-form';

// Espera o usuário parar de digitar antes de buscar, para não disparar uma requisição por tecla
const ATRASO_PESQUISA = 500;

interface IFormValues {
  [key: string]: string;
}

type InputProps = {
  label?: string;
  register?: UseFormRegister<IFormValues>;
  setFormValue?: UseFormSetValue<IFormValues>;
  setState?: React.Dispatch<React.SetStateAction<any>>;
  defaultValue?: string;
  readOnly?: boolean;
  // Registro escolhido de fora (ex.: cidade achada pelo CEP), aplicado como se fosse clicado na lista
  selecionado?: any;
  // Entidade da ficha: em compras, Fantasia é o fornecedor (codfor) e não o cliente
  entidade?: string;
};

function AutoCompleteInput({ label, register, setFormValue, setState, defaultValue, readOnly, selecionado, entidade, ...rest }: InputProps & React.InputHTMLAttributes<HTMLInputElement>) {
  const [sugestoes, setSugestoes] = useState<any[]>([]);
  const [value, setValue] = useState<string>(defaultValue || '');
  const idInput = useId();
  const timerPesquisa = useRef<ReturnType<typeof setTimeout>>();
  // Numera as buscas: só a última pode preencher a lista (respostas chegam fora de ordem)
  const ultimaPesquisa = useRef(0);

  const fornecedor = label === 'Fantasia' && entidade === 'compras';

  const campos = {
    Produto: 'mercador',
    Cidade: 'codcid',
    Vendedor: 'codven',
    Fantasia: fornecedor ? 'codfor' : 'codcli',
    Cliente: 'codcli',
    // No laytel do mercador o campo é a descrição (secmer...); o código vai para a coluna cod*
    'Seção': 'codsecmer',
    'SubSeção': 'codsubsecmer',
    'Classificação Fiscal': 'codclafis',
    Origem: 'codorimer',
  };

  const campo = campos[label as keyof typeof campos] || rest.name || '';

  async function pesquisa(termo: string, conferencia: boolean = false) {
    try {
      let endpoint = 'naoencontrado';

      switch (label) {
        case 'Produto':
          endpoint = '/mercador';
          break;
        case 'Cidade':
          endpoint = '/cidade';
          break;
        case 'Vendedor':
          endpoint = '/vendedor';
          break;
        case 'Fantasia':
          endpoint = fornecedor ? '/forneced' : '/cliente';
          break;
        case 'Cliente':
          endpoint = '/cliente';
          break;
        case 'Seção':
          endpoint = '/secmer';
          break;
        case 'SubSeção':
          endpoint = '/subsecmer';
          break;
        case 'Classificação Fiscal':
          endpoint = '/clafis';
          break;
        case 'Origem':
          endpoint = '/orimer';
          break;
        default:
          break;
      }

      if (endpoint === 'naoencontrado') return toast.error('Tabela não encontrada');

      const numero = ++ultimaPesquisa.current;

      const response = await api.get(endpoint, {
        params: { pesquisa: termo }
      });

      if (!conferencia && numero !== ultimaPesquisa.current) return;

      if (response.status === 200) {
        if (conferencia && setFormValue && response.data?.data && response.data.data.length > 0) {
          // A pesquisa é por "contém": prefere o registro com a descrição igual à gravada
          const lista: any[] = response.data.data;
          const igual = lista.find(item => Object.values(item).some(valor => String(valor ?? '').trim().toUpperCase() === termo.trim().toUpperCase()));
          handleSelect(sugestaoLabel(igual || lista[0]));
          return;
        }

        setSugestoes(response.data.data);
      }
    } catch (error) {
      console.error(error);
      toast.error('Erro ao buscar');
    }
  }

  // Descarta a busca agendada e ignora a que estiver em andamento
  function cancelarPesquisa() {
    clearTimeout(timerPesquisa.current);
    ultimaPesquisa.current++;
  }

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const value = e.target.value;

    if (!value) {
      cancelarPesquisa();
      setValue('');
      setSugestoes([]);
      // apagar o texto também limpa o código no formulário
      if (setFormValue) setFormValue(campo, '');
      return;
    }

    setValue(value);
    clearTimeout(timerPesquisa.current);
    timerPesquisa.current = setTimeout(() => pesquisa(value), ATRASO_PESQUISA);

    if (setFormValue) setFormValue(campo, value);
  }

  function handleSelect(selectedValue: any | string) {
    setValue(
      typeof selectedValue == 'object' ?
        sugestaoLabel(label) :
        // A sugestão é '<código> <descrição>': mostra só a descrição
        selectedValue.replace(/^\S+\s*/, '')
    );

    cancelarPesquisa();
    setSugestoes([]);

    if (setFormValue) setFormValue(campo, label === 'Produto' ? selectedValue.cod : selectedValue.trim().split(/\s+/)[0]);

    if (setState) {
      setState(selectedValue);
    }
  }

  function sugestaoLabel(sugestao: any) {
    switch (label) {
      case 'Produto':
        return `${sugestao.codbar} ${sugestao.mer} ${sugestao?.codtam || ''} ${sugestao?.padmer || ''}`
      case 'Cidade':
        return `${sugestao.cod} ${sugestao.cid} - ${sugestao.uf}`
      case 'Vendedor':
        return `${sugestao.cod} ${sugestao.ven}`
      case 'Fantasia':
      case 'Cliente':
        return `${sugestao.cod} ${sugestao.fan}`
      case 'Seção':
        return `${sugestao.cod} ${sugestao.secmer}`
      case 'SubSeção':
        return `${sugestao.cod} ${sugestao.subsecmer}`
      case 'Classificação Fiscal':
        return `${sugestao.cod} ${sugestao.clafis}`
      case 'Origem':
        return `${sugestao.cod} ${sugestao.orimer}`
      default:
        return label;
    }
  }

  useEffect(() => {
    if (defaultValue) {
      if (register) {
        pesquisa(defaultValue, true);
        return;
      }

      setValue(defaultValue);
    }
  }, [defaultValue]);

  useEffect(() => {
    if (selecionado) handleSelect(label === 'Produto' ? selecionado : sugestaoLabel(selecionado));
  }, [selecionado]);

  useEffect(() => () => clearTimeout(timerPesquisa.current), []);

  // Fecha a lista ao sair do campo ou com Esc
  function fecharSugestoes() {
    cancelarPesquisa();
    setSugestoes([]);
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Escape') fecharSugestoes();
  }

  return (
    <Container {...rest}>
      {register ?
        <Input
          {...register!(campo)}
          id={idInput}
          value={value}
          onChange={handleChange}
          onBlur={fecharSugestoes}
          onKeyDown={handleKeyDown}
          placeholder=' '
          autoComplete='off'
        /> :
        <Input
          id={idInput}
          value={value}
          onChange={handleChange}
          onBlur={fecharSugestoes}
          onKeyDown={handleKeyDown}
          placeholder=' '
          autoComplete='off'
          readOnly={readOnly}
        />
      }
      <label htmlFor={idInput}>{label}</label>
      {sugestoes && sugestoes.length > 0 && (
        <ContainerSugestoes role='listbox'>
          {sugestoes.map((sugestao, index) => (
            <Sugestao
              key={index}
              role='option'
              // mousedown antes do blur do input, senão a lista some antes do clique
              onMouseDown={e => e.preventDefault()}
              onClick={() => handleSelect(
                label === 'Produto' ?
                  sugestao :
                  sugestaoLabel(sugestao)
              )}
            >
              {sugestaoLabel(sugestao)}
            </Sugestao>
          ))}
        </ContainerSugestoes>
      )}
    </Container>
  );
};

export default AutoCompleteInput;
